import { cotizaTodas } from '@/services/api';
import type { CotizaTodasResponse } from '@/types/quote.types';
import { buildCombinedQuoteError, buildSingleCompanyError } from './fetchErrors';
import type { FetchQuotesParams, QuoteGenerationContext } from './types';

// The backend needs a moment to fully process the Update before cotizaTodas.
const BACKEND_SETTLE_MS = 2000;

interface CompanyOutcome {
  result: CotizaTodasResponse | null;
  errorMessage: string | null;
}

const fetchCompanySafely = async (
  { idCotizacion, formaPago, paquete }: FetchQuotesParams,
  company: 'QUALITAS' | 'ANA',
  displayName: string,
): Promise<CompanyOutcome> => {
  try {
    const result = await cotizaTodas({ idCotizacion, formaPago, paquete, company });
    return { result, errorMessage: null };
  } catch (err) {
    console.error(`❌ Error al obtener cotización inicial de ${company === 'ANA' ? 'ANA' : 'Qualitas'}:`, err);
    const errorMessage = err instanceof Error ? err.message : `Error al obtener cotización de ${displayName}`;
    return { result: null, errorMessage };
  }
};

const fetchBothCompanies = async (
  { state }: QuoteGenerationContext,
  params: FetchQuotesParams,
): Promise<void> => {
  const [qualitas, ana] = await Promise.all([
    fetchCompanySafely(params, 'QUALITAS', 'Qualitas'),
    fetchCompanySafely(params, 'ANA', 'ANA Seguros'),
  ]);
  state.quotesResponse.value = qualitas.result;
  state.anaQuotes.value = ana.result;

  // Only surface an error when both insurers failed.
  const bothFailed = !qualitas.result && !ana.result;
  state.error.value = bothFailed
    ? buildCombinedQuoteError(qualitas.errorMessage, ana.errorMessage)
    : null;
};

const fetchSingleCompany = async (
  { state }: QuoteGenerationContext,
  { idCotizacion, formaPago, paquete, company }: FetchQuotesParams,
): Promise<void> => {
  try {
    // BOTH never reaches this path (see shouldFetchBoth).
    const target = company === 'ANA' ? 'ANA' : 'QUALITAS';
    const response = await cotizaTodas({ idCotizacion, formaPago, paquete, company: target });
    state.quotesResponse.value = response;
    if (target === 'ANA') state.anaQuotes.value = response;
  } catch (companyError) {
    state.error.value = buildSingleCompanyError(companyError, company);
    throw companyError;
  }
};

const shouldFetchBoth = ({ company, showGlobalLoading }: FetchQuotesParams): boolean =>
  company === 'BOTH' || (showGlobalLoading && company === 'QUALITAS');

/** Fetch quotes: Qualitas + ANA in parallel on first load, one company on re-quote. */
export const runFetchQuotes = async (
  ctx: QuoteGenerationContext,
  params: FetchQuotesParams,
): Promise<void> => {
  const { state, loading } = ctx;
  state.isLoading.value = true;
  state.isFetchingQuotes.value = true; // Drives the results overlay
  state.error.value = null;
  if (params.showGlobalLoading) {
    loading.showLoading('Obteniendo cotizaciones...', 'Consultando con las aseguradoras...');
  }

  try {
    await new Promise((resolve) => setTimeout(resolve, BACKEND_SETTLE_MS));
    await (shouldFetchBoth(params) ? fetchBothCompanies(ctx, params) : fetchSingleCompany(ctx, params));
    if (params.showGlobalLoading) loading.hideLoading();
  } catch (err) {
    state.error.value = err instanceof Error ? err.message : 'Error al obtener las cotizaciones';
    console.error('❌ Error al obtener las cotizaciones:', err);
    if (params.showGlobalLoading) loading.hideLoading();
    throw err;
  } finally {
    state.isLoading.value = false;
    state.isFetchingQuotes.value = false;
  }
};
